import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Project } from 'src/app/model/project/project';
import ProjectInvoice from 'src/app/model/invoice/project-invoice';
import Product from 'src/app/model/product/product';
import { ProductTypes } from 'src/app/model/product/product-type';
import { FormatDecimal } from 'src/app/pipes/format-decimal';
import { PriceLine } from 'src/app/model/price-line';
import CustomProductInvoicePrice from 'src/app/model/invoice/custom-product-invoice-price';
import PlacementInvoice from 'src/app/model/invoice/placement-invoice';
import { Subject } from 'rxjs';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { SubProject } from '../../../../../model/project/sub-project';
import Company from 'src/app/model/company/company';
import { BoardPartAddedEvent } from 'src/app/events/board-part-added';
import { StandardStickerPartAddedEvent } from 'src/app/events/standard-sticker-part-added';
import ProductHelper from 'src/app/helpers/product-helper';
import Windowsticker from 'src/app/model/product/windowsticker/windowsticker';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { StandardStickerPartDeletedEvent } from 'src/app/events/standard-sticker-part-deleted';
import { ProductDeletedService } from 'src/app/services/product-deleted-service';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';

@Component({
  selector: 'company-invoice',
  templateUrl: './company-invoice.component.html',
  styleUrls: ['./company-invoice.component.scss'],
  providers: [FormatDecimal],
})
export class CompanyInvoiceComponent
  extends SubscriptionBasedComponent
  implements OnInit, OnChanges
{
  @Input()
  public project: Project;
  @Input()
  public inputProject: SubProject;
  @Input()
  public inputInvoice: ProjectInvoice;
  @Input()
  public invoiceType: string;
  @Input()
  public involvedCompanies: Company[];
  @Input()
  public updateEmitter: EventEmitter<any>;
  @Input()
  public productAdded: Subject<Product>;
  @Input()
  public boardPartAdded: Subject<BoardPartAddedEvent>;
  @Input()
  public standardStickerPartAdded: Subject<StandardStickerPartAddedEvent>;
  @Input()
  public standardStickerPartDeleted: Subject<StandardStickerPartDeletedEvent>;

  init = false;
  placementLine: PriceLine;
  productToPriceLines = new Map<number, PriceLine[]>();
  productToUsePlacementLine = new Map<number, boolean>();
  productToPlacementLine = new Map<number, PriceLine>();
  customProductPrice = new Map<number, CustomProductInvoicePrice>();

  companyFactor: number = 1;

  constructor(
    private productDeletedService: ProductDeletedService,
    private projectDisplayObservableService: ProjectDisplayObservableService
  ) {
    super();
  }

  ngOnInit() {
    this.initInvoiceLogic();
    this.companyFactor = CollectionHelper.isPopulated(this.involvedCompanies)
      ? 1 / this.involvedCompanies.length
      : 1;
    if (this.productAdded != null) {
      this.addSubscription(this.productAdded, (p) => {
        this.handleNewProductPrice(p);
        this.addCustomPriceForProduct(p);
      });
    }
    if (this.standardStickerPartAdded != null) {
      this.addSubscription(this.standardStickerPartAdded, (e) => {
        this.handleStandardStickerPartAdded(e);
      });
    }
    if (this.standardStickerPartDeleted != null) {
      this.addSubscription(this.standardStickerPartDeleted, (e) => {
        this.handleStandardStickerPartDeleted(e);
      });
    }
    this.addSubscription(
      this.productDeletedService.getProductDeletedSubscribable(),
      (e) => {
        this.handleProductDeleted(e);
      }
    );
    this.init = true;
  }

  ngOnChanges(changes: SimpleChanges) {
    const companies = changes['involvedCompanies'];
    if (
      !companies.isFirstChange() &&
      CollectionHelper.isPopulated(companies.currentValue)
    ) {
      this.companyFactor = CollectionHelper.isPopulated(this.involvedCompanies)
        ? 1 / this.involvedCompanies.length
        : 1;
    }
  }

  initInvoiceLogic() {
    if (this.inputInvoice.priceLines != null) {
      for (let i = 0; i < this.inputInvoice.priceLines.length; i++) {
        let line = this.inputInvoice.priceLines[i];
        if (
          this.inputInvoice.type === 'PlacementInvoice' &&
          line.placementLine
        ) {
          this.productToPlacementLine.set(line.product.id, line);
          this.productToUsePlacementLine.set(line.product.id, true);
        }
        if (!line.placementLine) {
          let productLines = this.productToPriceLines.get(line.product.id);
          if (productLines == null) {
            productLines = [];
            this.productToPriceLines.set(line.product.id, productLines);
          }
          productLines.push(line);
        }
      }
    } else {
      this.inputInvoice.priceLines = [];
    }
    if (this.inputInvoice.type === 'PlacementInvoice') {
      let placementInvoice = <PlacementInvoice>this.inputInvoice;
      if (this.project.products != null) {
        for (let product of this.project.products) {
          if (this.productToPlacementLine.get(product.id) == null) {
            let placementLine = new PriceLine();
            placementLine.product = product;
            placementLine.placementLine = true;
            this.productToPlacementLine.set(product.id, placementLine);
          }
          if (ProductHelper.isWindowSticker(product)) {
            const windowSticker = <Windowsticker>product;
            if (
              windowSticker.standardStickerParts != null &&
              windowSticker.standardStickerParts.length > 1
            ) {
              const previousToUse = this.productToUsePlacementLine.get(
                windowSticker.id
              );
              this.productToUsePlacementLine.set(windowSticker.id, true);
              if (!previousToUse) {
                this.inputInvoice.priceLines.push(
                  this.productToPlacementLine.get(windowSticker.id)
                );
              }
            }
          }
          if (
            product.type === 'CustomWindowSticker' ||
            product.type === 'OtherProduct'
          ) {
            let customProductInvoicePrice =
              placementInvoice.customProductInvoicePrices.find(
                (price) => price.product.id === product.id
              );
            if (customProductInvoicePrice == null) {
              customProductInvoicePrice =
                this.createCustomPriceForProduct(product);
              placementInvoice.customProductInvoicePrices.push(
                customProductInvoicePrice
              );
            }
            this.customProductPrice.set(product.id, customProductInvoicePrice);
          }
        }
      }
    }
    for (let product of this.project.products) {
      if (this.productToPriceLines.get(product.id) == null) {
        this.productToPriceLines.set(product.id, []);
      }
    }
  }

  isWindowSticker(product: Product) {
    return product.type === ProductTypes.WINDOWSTICKER.value;
  }

  isCustomWindowSticker(product: Product) {
    return product.type === ProductTypes.CUSTOMWINDOWSTICKER.value;
  }

  isBoard(product: Product) {
    return product.type === ProductTypes.BOARD.value;
  }

  isOtherProduct(product: Product) {
    return product.type === ProductTypes.OTHER_PRODUCT.value;
  }

  usePlacementLineChanged(value, productId) {
    value = value.checked;
    let index = this.inputInvoice.priceLines.findIndex((l) => {
      return l.placementLine && l.product.id === productId;
    });
    if (!value) {
      if (index >= 0) {
        this.inputInvoice.priceLines.splice(index, 1);
      }
    } else if (index < 0) {
      this.inputInvoice.priceLines.push(
        this.productToPlacementLine.get(productId)
      );
    }
    this.productToUsePlacementLine.set(productId, value);
    this.projectDisplayObservableService
      .getProjectInvoicePriceChangedSubscribable()
      .next();
    this.updateEmitter.emit();
  }

  placementLineChanged() {
    this.projectDisplayObservableService
      .getProjectInvoicePriceChangedSubscribable()
      .next();
    this.updateEmitter.emit();
  }

  customPriceChange() {
    this.projectDisplayObservableService
      .getProjectInvoicePriceChangedSubscribable()
      .next();
    this.updateEmitter.emit();
  }

  referenceNumberChanged() {
    this.updateEmitter.emit();
  }

  combinePrices(list, productId, productType) {
    let outputList = [];
    this.project.products.forEach((p) => {
      if (this.productToUsePlacementLine.get(p.id)) {
        outputList.push(this.productToPlacementLine.get(p.id));
      }
    });
    if (productType === ProductTypes.BOARD.value && productId != null) {
      this.productToPriceLines.set(productId, list);
    }
    this.productToPriceLines.forEach((pl) => {
      outputList = outputList.concat(pl);
    });
    if (
      productId != null &&
      (productType === ProductTypes.WINDOWSTICKER.value ||
        productType === ProductTypes.CUSTOMWINDOWSTICKER.value) &&
      !this.productToPriceLines.has(productId)
    ) {
      this.productToPriceLines.set(productId, list);
      outputList = outputList.concat(list);
    }
    this.inputInvoice.priceLines = outputList;
    this.projectDisplayObservableService
      .getProjectInvoicePriceChangedSubscribable()
      .next();
    this.updateEmitter.emit();
  }

  private handleProductDeleted(id: number) {
    if (id == null) {
      return;
    }
    this.productToPriceLines.delete(id);
    this.productToPlacementLine.delete(id);
    this.productToUsePlacementLine.delete(id);
    this.customProductPrice.delete(id);
  }

  private handleNewProductPrice(product: Product) {
    this.productToPriceLines.set(product.id, []);
    let placementLine = new PriceLine();
    placementLine.product = product;
    placementLine.placementLine = true;
    this.productToPlacementLine.set(product.id, placementLine);
    this.productToUsePlacementLine.set(product.id, false);
  }

  private handleStandardStickerPartAdded(event: StandardStickerPartAddedEvent) {
    this.usePlacementLineChanged({ checked: true }, event.windowStickerId);
  }

  private handleStandardStickerPartDeleted(
    event: StandardStickerPartDeletedEvent
  ) {
    if (
      event.sticker.id != null &&
      event.sticker.standardStickerParts != null &&
      event.sticker.standardStickerParts.length === 1
    ) {
      const placementLine = this.productToPlacementLine.get(event.sticker.id);
      if (placementLine != null && placementLine.price == null) {
        this.usePlacementLineChanged({ checked: false }, event.sticker.id);
      }
    }
  }

  private addCustomPriceForProduct(product: Product) {
    if (
      product.type !== 'CustomWindowSticker' &&
      product.type !== 'OtherProduct'
    ) {
      return;
    }
    let placementInvoice = <PlacementInvoice>this.inputInvoice;
    let customProductInvoicePrice = this.createCustomPriceForProduct(product);
    if (placementInvoice.customProductInvoicePrices == null) {
      placementInvoice.customProductInvoicePrices = [];
    }
    placementInvoice.customProductInvoicePrices.push(customProductInvoicePrice);
    this.customProductPrice.set(product.id, customProductInvoicePrice);
  }

  private createCustomPriceForProduct(
    product: Product
  ): CustomProductInvoicePrice {
    let customProductInvoicePrice = new CustomProductInvoicePrice();
    customProductInvoicePrice.product = new Product();
    customProductInvoicePrice.product.type = product.type;
    customProductInvoicePrice.product.id = product.id;
    customProductInvoicePrice.placementInvoice = new PlacementInvoice();
    customProductInvoicePrice.placementInvoice.id = this.inputInvoice.id;
    customProductInvoicePrice.productionPrice = '0';
    customProductInvoicePrice.placementPrice = '0';
    return customProductInvoicePrice;
  }
}
